import { useMutation } from "@apollo/client";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import Close from "@mui/icons-material/Close";
import { Alert, Box, Button, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { gql } from "generated-graphql";
import { ScoutProUpsellPlacement } from "generated-graphql/graphql";
import { useState } from "react";

import type { ScoutProUpsell } from "./scoutProUpsell";

import { useScoutHostContext } from "./ScoutHostContext";
import { useScoutProUpsellContext } from "./ScoutProUpsellContext";

const TRACK_SCOUT_PRO_UPSELL_LEARN_MORE = gql(`
  mutation TrackScoutProUpsellLearnMore($input: TrackScoutProUpsellLearnMoreInput!) {
    trackScoutProUpsellLearnMore(input: $input) {
      ok
    }
  }
`);

type Props = {
  upsell: ScoutProUpsell;
};

const LEARN_MORE_URL = "https://encamp.com/compliance-platform-foundation/";

export function ScoutProUpsellCard({ upsell }: Props) {
  const context = useScoutProUpsellContext();
  const { host } = useScoutHostContext();
  const [hasError, setHasError] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [trackLearnMore] = useMutation(TRACK_SCOUT_PRO_UPSELL_LEARN_MORE);

  const dismissed = context?.dismissedUpsells[upsell.upsellId] === "dismissed";
  const hasContext = context !== undefined;

  if (dismissed) {
    return null;
  }

  const handleDismiss = async () => {
    if (!context || isDismissing) return;
    setHasError(false);
    setIsDismissing(true);
    try {
      await context.onDismiss({ upsellId: upsell.upsellId });
    } catch {
      setHasError(true);
      setIsDismissing(false);
    }
  };

  const handleLearnMoreClick = () => {
    // Fire-and-forget: don't block the navigation to LEARN_MORE_URL on the
    // PostHog capture. Server resolves tenant from the `x-encamp-tenant-id`
    // header via `context.currentTenantId`, so we don't pass it here.
    void trackLearnMore({
      variables: {
        input: {
          upsellId: upsell.upsellId,
          capability: upsell.capability,
          host,
          placement: ScoutProUpsellPlacement.AssistantMessageCard,
        },
      },
    });
  };

  return (
    <Box
      data-testid="scout-pro-upsell-card"
      sx={(theme) => ({
        mt: 1,
        p: 2,
        borderRadius: 4,
        border: 1,
        borderColor: "divider",
        bgcolor: theme.palette.background.paper,
        position: "relative",
      })}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <AutoAwesome color="primary" fontSize="small" sx={{ mt: 0.25 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Unlock this with Scout Pro
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {upsell.message}
            </Typography>
          </Box>
          <IconButton
            aria-label="Dismiss Scout Pro upsell"
            size="small"
            disabled={!hasContext || isDismissing}
            onClick={handleDismiss}
            sx={{ ml: 1, mt: -0.5 }}
          >
            {isDismissing ? <CircularProgress size={16} /> : <Close fontSize="small" />}
          </IconButton>
        </Stack>
        {hasError ? (
          <Alert severity="error">Something went wrong dismissing this. Please try again.</Alert>
        ) : null}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            href={LEARN_MORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="text"
            size="small"
            onClick={handleLearnMoreClick}
          >
            LEARN MORE
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
